import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import classes from './SignupPage.module.scss';
import Button from '@/components/Button/Button';
import Language from '@/utils/Language';
import t from '@/utils/Language/Modal/LoginModal-translations';
import { useForm, Controller } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import useRecaptcha from '@/hooks/useRecaptcha';
import useAccount from '@/hooks/useAccount';
import useSaml from '@/hooks/useSaml';
import Disclaimer from '@/components/Disclaimer/Disclaimer';
import { regexFilledField, emailOrPhone } from '@/utils/utils';
import { useRouter } from 'next/router';
import { modalContext } from '@/context/modal.context';

const SignupForm = ({ signupArgs = {}, startingEmailOrPhoneInputValue, onSignupComplete, loginModalProps, context, reason, ...props }) => {
  const { register, control, formState } = useForm();
  const [onPasswordInput, setOnPasswordInput] = useState(false);
  const { enqueueModal } = useContext(modalContext);
  const [onRePasswordInput, setOnRePasswordInput] = useState(false);
  const [validPassword, setValidPassword] = useState(false);
  const [validEmailOrPhone, setValidEmailOrPhone] = useState('');
  const [matchPassword, setMatchPassword] = useState(false);
  const [passwords, setPasswords] = useState({ password: '', repassword: '' });
  const [isProfessional, setIsProfessional] = useState(!!signupArgs?.signupInvitation?.is_professional);
  const [processing, setProcessing] = useState(false);
  const { signup } = useAccount();
  const { samlPartner, handleBlurOnEmailOrPhone, handleKeyPressOnEmailOrPhone, handleSAMLRedirect, samlConfig } = useSaml();
  const { requireCaptcha, onChangeCaptcha, captchaState } = useRecaptcha({ immediate: true });
  const router = useRouter();

  const passwordValidationString = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[/\W/g_])[A-Za-z\d/\W/g_]{8,}$/;

  const doSignup = (e) => {
    setProcessing(true);
    if (handleSAMLRedirect(e)) {
      return;
    }
    signup(e, signupArgs)
      .then((data) => {
        setProcessing(false);
        if (data?.user?.id) {
          onSignupComplete(data);
        }
      })
      .catch((error) => {
        setProcessing(false);
      });
  };

  const checkPassword = (field) => {
    const value = field?.value;
    const match = value.match(passwordValidationString);
    setPasswords(Object.assign(passwords, { [field?.id]: field?.value }));
    setValidPassword(match !== null);
    setMatchPassword(isPasswordMatched());
  };

  const isPasswordMatched = () => {
    return passwords['password'] === passwords['repassword'];
  };

  const openLogin = (e) => {
    e.preventDefault();
    if (context == 'modal') {
      enqueueModal(
        {
          name: 'LoginModal',
          startingEmailOrPhoneInputValue,
          ...loginModalProps,
          backdrop: typeof props.backdrop !== 'undefined' ? props.backdrop : true,
          preventClose: props.preventClose
        },
        { forceOpen: true }
      );
    } else {
      router.push('/login');
    }
  };

  const toggleIsProfessional = (e) => {
    setIsProfessional(e.target.checked);
  };

  const validateSignupInput = (field) => {
    const value = field?.value;
    setValidEmailOrPhone(emailOrPhone(value));
  };

  useEffect(() => {
    setValidEmailOrPhone(emailOrPhone(startingEmailOrPhoneInputValue));
  }, [startingEmailOrPhoneInputValue]);

  return (
    <>
      {samlPartner === false && (
        <>
          <p>
            {t('Already have an account?')}{' '}
            <a href="#" onClick={openLogin}>
              {t('Log in')}
            </a>
          </p>
          <p className={classes.divider}>
            <span className={[`${classes.dividerLine} ${classes.dividerLineL}`]}></span>
            <span className={classes.dividerText}>{t('or')}</span>
            <span className={[`${classes.dividerLine} ${classes.dividerLineR}`]}></span>
          </p>
        </>
      )}
      {samlPartner !== false && <p dangerouslySetInnerHTML={{ __html: t(samlPartner.login_prompt_key) }} />}
      <Form data-cy="modal-signup-form" onSubmit={doSignup}>
        <Form.Group className="mb-3" controlId="formBasicEmail">
          <Controller
            control={control}
            name="emailOrPhone"
            defaultValue={startingEmailOrPhoneInputValue}
            rules={{ required: 'email or cell phone is required' }}
            render={({ field: { onChange, value, ref }, fieldState: { invalid } }) => (
              <>
                <Form.Control
                  {...register('emailOrPhone', { onBlur: handleBlurOnEmailOrPhone, required: true })}
                  onChange={(e) => {
                    validateSignupInput(e.target);
                    onChange(e);
                  }}
                  onKeyPress={handleKeyPressOnEmailOrPhone}
                  data-cy="modal-signup-input-email-or-phone"
                  value={value}
                  ref={ref}
                  type="text"
                  isInvalid={invalid}
                  placeholder={t('Email or cell phone')}
                  pattern={regexFilledField}
                />
                <label>{t('Email or cell phone')}</label>
              </>
            )}
          />
        </Form.Group>
        {samlPartner === false && (
          <>
            <Form.Group className="mb-3" controlId="password">
              <Controller
                control={control}
                name="password"
                defaultValue=""
                rules={{
                  required: 'password is required',
                  minLength: 8
                }}
                render={({ field: { onChange, onBlur, value, ref }, fieldState: { invalid } }) => (
                  <>
                    <Form.Control
                      data-cy="modal-signup-input-password"
                      {...register('password', { required: true })}
                      value={value}
                      ref={ref}
                      type="password"
                      isInvalid={invalid}
                      placeholder={t('password')}
                      pattern={regexFilledField}
                      onBlur={(e) => {
                        setOnPasswordInput(false);
                        onBlur(e);
                      }}
                      onChange={(e) => {
                        checkPassword(e.target);
                        onChange(e);
                      }}
                      onFocus={(e) => {
                        setOnPasswordInput(true);
                      }}
                    />
                    <label>{t('password')}</label>
                  </>
                )}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="repassword">
              <Controller
                control={control}
                name="repassword"
                defaultValue=""
                rules={{
                  required: 're-password is required',
                  minLength: 8
                }}
                render={({ field: { onChange, onBlur, value, ref }, fieldState: { invalid } }) => (
                  <>
                    <Form.Control
                      data-cy="modal-signup-input-password-confirmation"
                      value={value}
                      ref={ref}
                      type="password"
                      isInvalid={invalid}
                      placeholder={t('Re-enter password')}
                      pattern={regexFilledField}
                      onBlur={(e) => {
                        setOnRePasswordInput(false);
                        onBlur(e);
                      }}
                      onChange={(e) => {
                        checkPassword(e.target);
                        onChange(e);
                      }}
                      onFocus={(e) => {
                        setOnRePasswordInput(true);
                      }}
                    />
                    <label>{t('Re-enter password')}</label>
                  </>
                )}
              />
            </Form.Group>
          </>
        )}
        {(onPasswordInput || onRePasswordInput) && !validPassword && (
          <div data-cy="modal-signup-tooltip-password-requirements" className={classes.tooltip}>
            <p>{t('at least')}</p>
            <ul>
              <li>{t('8 characters')}</li>
              <li>{t('1 letter')}</li>
              <li>{t('1 number')}</li>
              <li>{t('1 special character')}</li>
            </ul>
          </div>
        )}
        {onRePasswordInput && !matchPassword && (
          <div data-cy="modal-signup-tooltip-password-match" className={[`${classes.tooltip} ${classes.rePasswordTooltip}`]}>
            <p>{t('The passwords should match')}</p>
          </div>
        )}
        <p className={classes.errorMessages}>
          {formState.errors.email && <span>{t('Email or cell phone is required')}</span>}
          {formState.errors.email && formState.errors.password && <br />}
          {formState.errors.password && <span>{t('Password is required')}</span>}
        </p>
        {samlPartner === false && (
          <Form.Group className="mb-3" controlId="formBasicCheckbox">
            <Form.Check
              data-cy="modal-signup-checkbox-pro"
              type="checkbox"
              label={t('work_at_nonprofit')}
              {...register('is_professional', { required: false })}
              onChange={toggleIsProfessional}
              checked={isProfessional}
            />
          </Form.Group>
        )}
        {requireCaptcha && !processing && (
          <div>
            <ReCAPTCHA
              className={classes.captcha}
              hl={Language.getCurrentLanguage()}
              sitekey={process.env.RECAPTCHA_PUBLIC_KEY}
              onChange={onChangeCaptcha}
            />
          </div>
        )}

        <Button
          type="primary"
          disabled={
            ((!validPassword || !matchPassword) && samlPartner === false) ||
            !validEmailOrPhone ||
            samlConfig === false ||
            processing ||
            (requireCaptcha && !captchaState)
          }
          styles={{ width: 'full' }}
          text={samlPartner !== false ? t(samlPartner.login_button_key) : t('Create an account')}
          dataCy={'modal-signup-btn-signup'}
          htmlType="submit"
        />
      </Form>
      {samlPartner === false && <Disclaimer></Disclaimer>}
    </>
  );
};

SignupForm.propTypes = {
  context: PropTypes.string,
  loginModalProps: PropTypes.object,
  onSignupComplete: PropTypes.func,
  signupArgs: PropTypes.object,
  startingEmailOrPhoneInputValue: PropTypes.string
};

SignupForm.defaultProps = {
  loginModalProps: {},
  onSignupComplete: () => {},
  signupArgs: {}
};

export default SignupForm;
